// Message input: Enter sends, Shift+Enter adds a line. Disabled while a turn
// is already running — the API would 409 on a second one for the same
// conversation anyway (07-chat.md §2.1), this just avoids the round trip.

import { useState, type KeyboardEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { chatApi } from '../../lib/api-client'

export function ChatComposer({
  conversationId,
  disabled,
  pageContext,
  activeTurnId,
  cancelRequested,
  onStop,
  stopping,
}: {
  conversationId: string
  /** True while the conversation already has a turn running. */
  disabled: boolean
  pageContext?: Record<string, unknown>
  /** The running turn's id, for the stop button (07 QA #5) — null/undefined while idle. */
  activeTurnId?: string | null
  /** True once a stop has been requested for `activeTurnId` and the worker hasn't finished stopping it yet. */
  cancelRequested?: boolean
  onStop?: (turnId: string) => void
  stopping?: boolean
}) {
  const [content, setContent] = useState('')
  const queryClient = useQueryClient()

  const send = useMutation({
    mutationFn: (text: string) =>
      chatApi.postMessage(conversationId, { content: text, pageContext }),
    onSuccess: () => {
      setContent('')
      void queryClient.invalidateQueries({ queryKey: ['chat-status', conversationId] })
      void queryClient.invalidateQueries({
        queryKey: ['chat-conversation', conversationId],
      })
    },
  })

  function submit(): void {
    const text = content.trim()
    if (!text || disabled || send.isPending) return
    send.mutate(text)
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      submit()
    }
  }

  return (
    <div className="border-t border-line p-3">
      {send.isError ? (
        <p className="mb-2 text-sm text-danger">
          {send.error instanceof Error ? send.error.message : 'Could not send that message.'}
        </p>
      ) : null}
      <div className="flex items-end gap-2">
        <textarea
          className="field-input min-h-[2.5rem] flex-1 resize-none py-2"
          rows={1}
          placeholder={
            disabled ? 'A turn is already running…' : 'Message the assistant…'
          }
          value={content}
          disabled={disabled || send.isPending}
          onChange={(event) => setContent(event.target.value)}
          onKeyDown={onKeyDown}
        />
        {disabled && activeTurnId ? (
          <button
            type="button"
            className="btn-danger-quiet btn-sm shrink-0"
            disabled={stopping || cancelRequested}
            onClick={() => onStop?.(activeTurnId)}
          >
            {cancelRequested ? 'Stopping…' : 'Stop'}
          </button>
        ) : (
          <button
            type="button"
            className="btn-primary btn-sm shrink-0"
            disabled={disabled || send.isPending || content.trim() === ''}
            onClick={submit}
          >
            Send
          </button>
        )}
      </div>
    </div>
  )
}
